import Button from '@doordash/component-button'
import { Text, StackChildren } from '@doordash/design-language'
import * as React from 'react'
import { connect } from 'react-redux'
import {
  ANDROID_CLOSE_DEEPLINK,
  DD_READY_FIRST_DASH_TS,
  IN_APP_EDUCATION_SUB,
  INSTANT_DASH_SPLASH_SUB,
  IOS_CLOSE_DEEPLINK,
  SEVEN_DAYS,
} from '../../../constants'
import * as events from '../../../events'
import routes from '../../../routes'
import SimpleStore from '../../../utilities/SimpleStore'
import {
  detectWebviewOs,
  getMobileDevice,
  MobileDevice,
  getDeviceVersion,
} from '../../../utilities/userAgent'
import { OsTypes } from '../../../webview/types'
import AppDownloadButton, {
  AppDownloadOS,
} from '../../atoms/app-download-button'
import LinkButton from '../../atoms/link-button'
import * as Exp from '../../../experimentConstants'
import { startDashing } from './strings'
import * as DA from '../../../typeModules/events'
import { get } from 'lodash'

const ANFD_ANDROID_VERSION = 6040029

type StartDashingStepProps = {
  isActive: boolean
  experiments: any
  trackConfirmationPageTapDownloadApp?: () => Promise<any>
  trackWebviewClosed?: () => Promise<any>
  isCompleted?: boolean
  trackActivatedNoFirstdash?: (
    name: DA.EventActions,
    page: DA.ActivatedNoFirstDashScreens,
    data?: object
  ) => Promise<void>
}
const os = detectWebviewOs()
const isWebview = os !== OsTypes.NONE
const device = getMobileDevice()

const getInAppEducation = props => {
  if (props.experiments.in_app_education_localstorage) {
    const firstDashTimestamp = SimpleStore.get(DD_READY_FIRST_DASH_TS) || false
    const isPastSevenDays =
      firstDashTimestamp &&
      Math.floor((Date.now() - firstDashTimestamp) / 1000) >= SEVEN_DAYS
    return (
      props.experiments.in_app_education &&
      (!firstDashTimestamp || isPastSevenDays)
    )
  }
  return props.experiments.in_app_education
}

const generateButton = props => {
  if (!props.isActive && !props.isCompleted) {
    return null
  }
  const isiOSOrAndroid =
    device === MobileDevice.Android || device === MobileDevice.iOS
  const inAppEducation = getInAppEducation(props)
  if (isWebview) {
    let isEligibileForActivatedNoFirstDash =
      props.experiments[Exp.DX_ACTIVATED_NO_FIRST_DASH]

    const androidANFDOverride = get(
      props.experiments,
      [Exp.AN_DX_ANFD_ONBOARDING],
      true
    )
    const deviceAppVersion = getDeviceVersion()

    if (isEligibileForActivatedNoFirstDash) {
      if (device === MobileDevice.Android) {
        props.trackActivatedNoFirstdash(
          DA.EventActions.Rendered,
          DA.ActivatedNoFirstDashScreens.Dropshipping,
          {
            deviceAppVersion,
            androidOverride: !androidANFDOverride,
          }
        )

        if (deviceAppVersion < ANFD_ANDROID_VERSION || !androidANFDOverride) {
          isEligibileForActivatedNoFirstDash = false
        }
      } else {
        // we need to disable this experiment for ios as the release isnt ready
        isEligibileForActivatedNoFirstDash = false
      }
    }

    if (isEligibileForActivatedNoFirstDash) {
      return (
        <LinkButton
          type={Button.Types.PrimaryPill}
          isInline={true}
          size={Button.Sizes.Small}
          href={`${routes.firstDash.path}?shouldShowNativeNavBar=false`}
        >
          {startDashing.getStartedButton()}
        </LinkButton>
      )
    }

    if (inAppEducation) {
      return (
        <LinkButton
          type={Button.Types.PrimaryPill}
          isInline={true}
          size={Button.Sizes.Small}
          onClick={e => e.stopPropagation()}
          href={routes.webview.buildPath({ sub: IN_APP_EDUCATION_SUB })}
        >
          {startDashing.getStartedButton()}
        </LinkButton>
      )
    }

    if (props.experiments.instant_dashing_v2) {
      return (
        <LinkButton
          type={Button.Types.PrimaryPill}
          isInline={true}
          size={Button.Sizes.Small}
          onClick={e => e.stopPropagation()}
          href={routes.webview.buildPath({ sub: INSTANT_DASH_SPLASH_SUB })}
        >
          {startDashing.getStartedButton()}
        </LinkButton>
      )
    }

    return (
      <Button
        type={Button.Types.PrimaryPill}
        isInline={true}
        size={Button.Sizes.Small}
        href={os === OsTypes.iOS ? IOS_CLOSE_DEEPLINK : ANDROID_CLOSE_DEEPLINK}
        tag="a"
        onClick={e => {
          e.stopPropagation()
          props.trackWebviewClosed()
        }}
      >
        {startDashing.getStartedButton()}
      </Button>
    )
  }

  if (isiOSOrAndroid) {
    return (
      <AppDownloadButton
        type={Button.Types.PrimaryPill}
        isInline={true}
        size={Button.Sizes.Small}
        os={
          device === MobileDevice.Android
            ? AppDownloadOS.ANDROID
            : AppDownloadOS.IOS
        }
        onClick={props.trackConfirmationPageTapDownloadApp}
      />
    )
  }
  return (
    <AppDownloadButton
      type={Button.Types.PrimaryPill}
      isInline={true}
      size={Button.Sizes.Small}
      os={AppDownloadOS.DESKTOP}
      onClick={props.trackConfirmationPageTapDownloadApp}
    />
  )
}

export const StartDashingStep: React.SFC<StartDashingStepProps> = props => {
  const { instant_dashing_v2: instantDashingV2 } = props.experiments

  let body
  if (isWebview) {
    body = instantDashingV2 ? startDashing.nowV2() : startDashing.now()
  } else if (props.isActive || props.isCompleted) {
    body = startDashing.body()
  } else {
    body = startDashing.future()
  }

  return (
    <StackChildren>
      <Text style={Text.Styles.SectionSubtext}>{body}</Text>
      {generateButton(props)}
    </StackChildren>
  )
}

StartDashingStep.displayName = 'StartDashingStep'

const mapStateToProps = state => ({
  experiments: state.experiments,
})

const mapDispatchToProps = {
  trackConfirmationPageTapDownloadApp:
    events.trackConfirmationPageTapDownloadApp,
  trackWebviewClosed: events.trackWebviewClosed,
  trackActivatedNoFirstdash: events.trackActivatedNoFirstDash,
}
export default connect(mapStateToProps, mapDispatchToProps)(StartDashingStep)
